import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { ArrowLeft, FileText, Lock, User as UserIcon, Calendar as CalendarIcon, ChevronDown, ChevronRight } from 'lucide-react'
import setThemeColor from '../lib/themeColor'
import useEventRole from '../lib/useEventRole'

// Dossier hub for a closed event. Lists every after-action document
// available — today that's the AAR; future expansions (mission packs,
// patrol reports, etc.) slot in here without changing the entry path.
const D = {
  page: { minHeight: '100vh', background: '#d8c898', color: '#1f1d18', fontFamily: "'Special Elite', 'Courier Prime', monospace" },
  nav: { background: '#1f1d18', borderBottom: '3px solid #c08a2a', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  paper: { background: '#f0e6c8', border: '1px solid rgba(120,100,60,.35)', boxShadow: '0 2px 8px rgba(0,0,0,.18), 0 1px 3px rgba(0,0,0,.12)', position: 'relative', overflow: 'hidden' },
  display: { fontFamily: "'Stardos Stencil', 'Special Elite', serif" },
  body: { fontFamily: "'Special Elite', 'Courier Prime', monospace" },
  ink2: { color: '#4a4636' }, ink3: { color: '#7a7058' }, stamp: { color: '#c08a2a' },
}

export default function EventDossiers() {
  const { id: eventId } = useParams()
  const { user } = useAuth()
  const navigate = useNavigate()
  const role = useEventRole(eventId)
  const [event, setEvent] = useState(null)
  const [aarExists, setAarExists] = useState(false)
  const [stats, setStats] = useState(null)        // v_player_event_stats row
  const [statsExpanded, setStatsExpanded] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => { setThemeColor('dossier') }, [])
  useEffect(() => { fetchAll() }, [eventId, user?.id])

  const fetchAll = async () => {
    setLoading(true)
    const [{ data: ev }, { data: aar }, { data: statRow }] = await Promise.all([
      supabase.from('events').select('id, name, status, closed_at, endex_at, location').eq('id', eventId).limit(1),
      supabase.from('event_aars').select('id').eq('event_id', eventId).limit(1),
      // Personal stat card pulls from v_player_event_stats — same view the
      // PlayerView career toggle reads. Returns null gracefully when the
      // viewer wasn't on the event roster.
      user?.id
        ? supabase.from('v_player_event_stats').select('*').eq('event_id', eventId).eq('user_id', user.id).limit(1)
        : Promise.resolve({ data: [] }),
    ])
    if (!ev?.[0]) { navigate('/play'); return }
    setEvent(ev[0])
    setAarExists((aar?.length || 0) > 0)
    setStats(statRow?.[0] || null)
    setLoading(false)
  }

  if (loading) {
    return (
      <div style={{ ...D.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ ...D.display, fontSize: 14, color: '#7a7058', letterSpacing: '0.14em' }}>OPENING ARCHIVE...</p>
      </div>
    )
  }

  const isClosed = event?.status === 'closed'

  return (
    <div style={D.page}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Stardos+Stencil:wght@400;700&family=Special+Elite&family=Courier+Prime:wght@400;700&display=swap');`}</style>

      <div style={D.nav}>
        <div>
          <div style={{ ...D.display, color: '#f0e6c8', fontSize: 14, letterSpacing: '0.14em' }}>ANUBIS</div>
          <div style={{ color: '#c08a2a', fontSize: 9, letterSpacing: '0.12em', marginTop: 2 }}>FIELD OPS · ARCHIVE</div>
        </div>
        <div style={{ color: '#c08a2a', fontSize: 9, letterSpacing: '0.08em' }}>
          {event?.name?.toUpperCase() || 'NO EVENT'}
        </div>
      </div>

      <button onClick={() => navigate(-1)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: 'none', border: 'none', color: '#7a7058', fontFamily: "'Special Elite', monospace", fontSize: 11, cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        <ArrowLeft size={14} /> RETURN
      </button>

      <div style={{ margin: '0 10px 16px', maxWidth: 720, marginInline: 'auto', ...D.paper }}>
        <div style={{ padding: '14px 16px', borderBottom: '2px solid #c08a2a' }}>
          <p style={{ ...D.body, fontSize: 10, color: '#7a7058', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 4 }}>OPERATION ARCHIVE</p>
          <div style={{ ...D.display, fontSize: 22, color: '#1f1d18', letterSpacing: '0.06em' }}>
            {event?.name || 'UNNAMED OPERATION'}
          </div>
          {event?.closed_at && (
            <p style={{ ...D.body, fontSize: 11, color: '#7a7058', marginTop: 4 }}>
              CLOSED · {new Date(event.closed_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
            </p>
          )}
          {!isClosed && (
            <p style={{ ...D.body, fontSize: 11, color: '#a82a1f', marginTop: 4 }}>
              Event status: {event?.status?.toUpperCase()}. Dossiers unlock once the event closes.
            </p>
          )}
        </div>

        <div style={{ padding: '14px 16px' }}>
          <p style={{ ...D.display, fontSize: 11, letterSpacing: '0.14em', color: '#4a4636', marginBottom: 10 }}>§ AVAILABLE DOSSIERS</p>

          {/* AAR — primary dossier */}
          <button
            onClick={() => isClosed && aarExists && navigate(`/event/${eventId}/aar`)}
            disabled={!isClosed || !aarExists}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%',
              padding: '14px 16px', textAlign: 'left',
              background: '#f0e6c8',
              border: '1px solid rgba(120,100,60,.35)',
              cursor: (isClosed && aarExists) ? 'pointer' : 'not-allowed',
              opacity: (isClosed && aarExists) ? 1 : 0.55,
              marginBottom: 10,
            }}
          >
            <FileText size={20} style={{ color: '#c08a2a', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ ...D.display, fontSize: 16, color: '#1f1d18', letterSpacing: '0.04em' }}>
                AFTER ACTION REPORT
              </p>
              <p style={{ ...D.body, fontSize: 11, color: '#7a7058', marginTop: 2 }}>
                Event timeline, faction summaries, mission outcomes
              </p>
            </div>
            {!isClosed || !aarExists ? (
              <Lock size={16} style={{ color: '#7a7058', flexShrink: 0 }} />
            ) : (
              <span style={{ ...D.display, fontSize: 11, color: '#c08a2a', letterSpacing: '0.14em', flexShrink: 0 }}>OPEN →</span>
            )}
          </button>

          {/* Personal Stat Card — inline expandable. Reads v_player_event_stats. */}
          <button
            onClick={() => stats && setStatsExpanded(v => !v)}
            disabled={!stats}
            style={{
              display: 'flex', alignItems: 'center', gap: 12, width: '100%',
              padding: '14px 16px', textAlign: 'left',
              background: '#f0e6c8',
              border: '1px solid rgba(120,100,60,.35)',
              cursor: stats ? 'pointer' : 'not-allowed',
              opacity: stats ? 1 : 0.55,
              marginBottom: 10,
            }}
          >
            <UserIcon size={20} style={{ color: '#c08a2a', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ ...D.display, fontSize: 16, color: '#1f1d18', letterSpacing: '0.04em' }}>PERSONAL STAT CARD</p>
              <p style={{ ...D.body, fontSize: 11, color: '#7a7058', marginTop: 2 }}>
                Your event summary — kills, bounties, FRAGOs, intel, points
              </p>
            </div>
            {!stats ? (
              <Lock size={16} style={{ color: '#7a7058', flexShrink: 0 }} />
            ) : statsExpanded ? (
              <ChevronDown size={18} style={{ color: '#7a7058', flexShrink: 0 }} />
            ) : (
              <ChevronRight size={18} style={{ color: '#7a7058', flexShrink: 0 }} />
            )}
          </button>
          {stats && statsExpanded && (
            <div style={{ background: 'rgba(255,255,255,.5)', border: '1px solid rgba(120,100,60,.35)', borderTop: 'none', padding: 14, marginTop: -10, marginBottom: 10 }}>
              <p style={{ ...D.display, fontSize: 14, color: '#1f1d18', letterSpacing: '0.06em' }}>{stats.callsign || stats.display_name || 'Operator'}</p>
              <p style={{ ...D.body, fontSize: 11, color: '#7a7058' }}>{stats.event_name}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginTop: 12 }}>
                {[
                  ['Points',     stats.total_points || 0,       '#c08a2a'],
                  ['Confirmed',  stats.kills_confirmed || 0,    '#3a6a3a'],
                  ['Unconfirmed',stats.kills_unconfirmed || 0,  '#7a7058'],
                  ['Deaths',     stats.deaths || 0,             '#a82a1f'],
                  ['Bounties',   stats.bounties_claimed || 0,   '#c08a2a'],
                  ['FRAGOs',     stats.fragos_completed || 0,   '#2a4a8c'],
                  ['Intel',      stats.intel_uploaded || 0,     '#3a6a3a'],
                  ['Survived',   stats.bounties_survived || 0,  '#1f1d18'],
                ].map(([label, val, color]) => (
                  <div key={label} style={{ textAlign: 'center' }}>
                    <p style={{ ...D.display, fontSize: 22, color, lineHeight: 1 }}>{val}</p>
                    <p style={{ ...D.body, fontSize: 9, color: '#7a7058', letterSpacing: '0.14em', textTransform: 'uppercase', marginTop: 4 }}>{label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Play Sheet — GM only. Read-only by the time the event closes,
              but still useful as a record of what was queued / deployed. */}
          {(role.isFullGM || role.isCoGm) && (
            <button
              onClick={() => navigate(`/event/${eventId}/playsheet`)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                padding: '14px 16px', textAlign: 'left',
                background: '#f0e6c8',
                border: '1px solid rgba(120,100,60,.35)',
                cursor: 'pointer',
                marginBottom: 10,
              }}
            >
              <CalendarIcon size={20} style={{ color: '#c08a2a', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ ...D.display, fontSize: 16, color: '#1f1d18', letterSpacing: '0.04em' }}>PLAY SHEET</p>
                <p style={{ ...D.body, fontSize: 11, color: '#7a7058', marginTop: 2 }}>
                  GM record — every queued + deployed item, in order
                </p>
              </div>
              <span style={{ ...D.display, fontSize: 11, color: '#c08a2a', letterSpacing: '0.14em', flexShrink: 0 }}>OPEN →</span>
            </button>
          )}

          {!isClosed && (
            <p style={{ ...D.body, fontSize: 11, color: '#7a7058', marginTop: 6, fontStyle: 'italic' }}>
              The AAR is generated when the GM closes the event. Check back later.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
